import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { ShieldOff, Loader2, Trash2, RefreshCw, CheckCircle2, MailX, FlaskConical } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OutOfScopePurgePage() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purging, setPurging] = useState(false);
  const [purgeResult, setPurgeResult] = useState(null);

  // Unreachable / incomplete records (commercial partners + investors)
  const [unreach, setUnreach] = useState(null);
  const [unreachLoading, setUnreachLoading] = useState(true);
  const [unreachPurging, setUnreachPurging] = useState(false);
  const [unreachResult, setUnreachResult] = useState(null);

  // Platform-only assets (non-licensable technology platforms)
  const [plat, setPlat] = useState(null);
  const [platLoading, setPlatLoading] = useState(true);
  const [platPurging, setPlatPurging] = useState(false);
  const [platResult, setPlatResult] = useState(null);

  const runPlatDryRun = useCallback(async () => {
    setPlatLoading(true);
    try {
      const { data } = await axios.post(`${API}/data-ops/scope-purge/platform-assets/run`, { dry_run: true }, { withCredentials: true });
      setPlat(data);
    } catch { toast.error('Failed to compute platform-assets report'); }
    finally { setPlatLoading(false); }
  }, []);

  const executePlatPurge = async () => {
    setPlatPurging(true);
    try {
      const { data } = await axios.post(`${API}/data-ops/scope-purge/platform-assets/run`, { dry_run: false }, { withCredentials: true });
      setPlatResult(data);
      toast.success(`Removed ${data.deleted?.assets || 0} platform asset(s)`);
      runPlatDryRun();
    } catch { toast.error('Platform-assets purge failed'); }
    finally { setPlatPurging(false); }
  };

  const runUnreachDryRun = useCallback(async () => {
    setUnreachLoading(true);
    try {
      const { data } = await axios.post(`${API}/data-ops/scope-purge/unreachable/run`, { dry_run: true }, { withCredentials: true });
      setUnreach(data);
    } catch { toast.error('Failed to compute unreachable-records report'); }
    finally { setUnreachLoading(false); }
  }, []);

  const executeUnreachPurge = async () => {
    setUnreachPurging(true);
    try {
      const { data } = await axios.post(`${API}/data-ops/scope-purge/unreachable/run`, { dry_run: false }, { withCredentials: true });
      setUnreachResult(data);
      toast.success(`Removed ${data.deleted?.commercial_partners || 0} partner(s) and ${data.deleted?.investors || 0} investor(s)`);
      runUnreachDryRun();
    } catch { toast.error('Unreachable-records purge failed'); }
    finally { setUnreachPurging(false); }
  };

  const runDryRun = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/data-ops/scope-purge/run`, { dry_run: true }, { withCredentials: true });
      setReport(data);
    } catch { toast.error('Failed to compute out-of-scope report'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { runDryRun(); runUnreachDryRun(); runPlatDryRun(); }, [runDryRun, runUnreachDryRun, runPlatDryRun]);

  const executePurge = async () => {
    setPurging(true);
    try {
      const { data } = await axios.post(`${API}/data-ops/scope-purge/run`, { dry_run: false }, { withCredentials: true });
      setPurgeResult(data);
      toast.success(`Purged ${data.deleted?.companies || 0} out-of-scope companies`);
      runDryRun();
    } catch { toast.error('Purge failed'); }
    finally { setPurging(false); }
  };

  return (
    <div className="p-8 max-w-4xl mx-auto space-y-6" data-testid="scope-purge-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <ShieldOff className="w-6 h-6 text-rose-500" /> Out-of-Scope Purge
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Permanently removes companies tagged exclusively as Formulation &amp; Delivery (NDDS), DTx / Companion Digital,
          Academic / University or Technology Platform — with no therapeutic assets. Purged records are archived to
          <code className="mx-1 text-xs bg-slate-100 px-1 rounded">excluded_companies_archive</code> before deletion.
          Hybrid companies (any therapeutic asset) and starred companies are always kept.
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : report && (
        <>
          <div className="grid grid-cols-3 gap-4">
            <Card className="border-slate-100"><CardContent className="p-4">
              <p className="text-2xl font-bold text-rose-600" data-testid="purgeable-count">{report.purgeable_companies}</p>
              <p className="text-xs text-slate-500">Companies to purge</p>
            </CardContent></Card>
            <Card className="border-slate-100"><CardContent className="p-4">
              <p className="text-2xl font-bold text-slate-900">{report.before?.biotech_companies}</p>
              <p className="text-xs text-slate-500">Biotech Companies (current)</p>
            </CardContent></Card>
            <Card className="border-slate-100"><CardContent className="p-4">
              <p className="text-2xl font-bold text-emerald-700">{report.after?.biotech_companies}</p>
              <p className="text-xs text-slate-500">After purge</p>
            </CardContent></Card>
          </div>

          <Card className="border-slate-100">
            <CardContent className="p-5 space-y-4">
              <div>
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Removal by category</p>
                {Object.keys(report.by_category || {}).length === 0 ? (
                  <p className="text-xs text-slate-400 flex items-center gap-1.5" data-testid="scope-clean-msg">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> Database is clean — no out-of-scope records found.
                  </p>
                ) : (
                  <div className="space-y-1.5" data-testid="category-breakdown">
                    {Object.entries(report.by_category).map(([cat, n]) => (
                      <div key={cat} className="flex items-center justify-between text-sm">
                        <span className="text-slate-700">{cat}</span>
                        <Badge variant="secondary" className="text-xs">{n}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {(report.sample || []).length > 0 && (
                <div>
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Sample (first 25)</p>
                  <div className="border border-slate-100 rounded-lg divide-y divide-slate-50 max-h-64 overflow-y-auto">
                    {report.sample.map((s, i) => (
                      <div key={i} className="flex items-center justify-between px-3 py-1.5 text-xs">
                        <span className="text-slate-700 truncate">{s.company_name}</span>
                        <span className="text-slate-400 shrink-0 ml-2">{(s.categories || []).join(', ')}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={runDryRun} className="text-xs" data-testid="rerun-dryrun-btn">
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Re-scan
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" disabled={purging || report.purgeable_companies === 0}
                  className="text-xs bg-rose-600 hover:bg-rose-700 text-white" data-testid="purge-btn">
                  {purging ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 mr-1.5" />}
                  Purge {report.purgeable_companies} companies permanently
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Purge {report.purgeable_companies} out-of-scope companies?</AlertDialogTitle>
                  <AlertDialogDescription>
                    They will be removed from all counts, searches, dashboards, enrichment and outreach.
                    Full records are archived to <code>excluded_companies_archive</code> first.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="purge-cancel-btn">Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={executePurge} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="purge-confirm-btn">
                    Purge permanently
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>

          {purgeResult && !purgeResult.dry_run && (
            <Card className="border-emerald-200 bg-emerald-50/40" data-testid="purge-result-card">
              <CardContent className="p-5 text-sm text-slate-700 space-y-1">
                <p className="font-semibold text-emerald-800 flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4" /> Purge complete
                </p>
                <p>Companies removed: <b>{purgeResult.deleted?.companies}</b> · Assets removed: <b>{purgeResult.deleted?.assets}</b></p>
                <p>Biotech Companies: {purgeResult.before?.biotech_companies} → <b>{purgeResult.after?.biotech_companies}</b></p>
                <p>Total Assets: {purgeResult.before?.assets} → <b>{purgeResult.after?.assets}</b></p>
                <p className="text-xs text-slate-500">Dashboard metrics recompute live — homepage numbers now reflect the final scope. Future conference imports skip these categories at ingestion.</p>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* ===== Unreachable / incomplete records ===== */}
      <div className="pt-4 border-t border-slate-100">
        <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <MailX className="w-5 h-5 text-rose-500" /> Unreachable Records
        </h2>
        <p className="text-sm text-slate-500 mt-1 mb-3">
          Name + email are mandatory for commercial partners and investors. This removes commercial partners
          with no email anywhere (profile or contacts) and investors missing a name or an email. Archived to
          <code className="mx-1 text-xs bg-slate-100 px-1 rounded">unreachable_purge_archive</code> first.
        </p>
        {unreachLoading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : unreach && (
          <>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <Card className="border-slate-100"><CardContent className="p-4">
                <p className="text-2xl font-bold text-rose-600" data-testid="unreach-partner-count">{unreach.unreachable_partners}</p>
                <p className="text-xs text-slate-500">Commercial partners without email ({unreach.before?.commercial_partners} total)</p>
              </CardContent></Card>
              <Card className="border-slate-100"><CardContent className="p-4">
                <p className="text-2xl font-bold text-rose-600" data-testid="unreach-investor-count">{unreach.incomplete_investors}</p>
                <p className="text-xs text-slate-500">Investors missing name or email ({unreach.before?.investors} total)</p>
              </CardContent></Card>
            </div>
            {((unreach.partner_sample || []).length > 0 || (unreach.investor_sample || []).length > 0) && (
              <Card className="border-slate-100 mb-4"><CardContent className="p-4 space-y-3">
                {(unreach.partner_sample || []).length > 0 && (
                  <div>
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Commercial partners (first 25)</p>
                    <div className="border border-slate-100 rounded-lg divide-y divide-slate-50 max-h-40 overflow-y-auto">
                      {unreach.partner_sample.map((s, i) => (
                        <div key={i} className="px-3 py-1.5 text-xs text-slate-700 truncate">{s.company_name}</div>
                      ))}
                    </div>
                  </div>
                )}
                {(unreach.investor_sample || []).length > 0 && (
                  <div>
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Investors (first 25)</p>
                    <div className="border border-slate-100 rounded-lg divide-y divide-slate-50 max-h-40 overflow-y-auto">
                      {unreach.investor_sample.map((s, i) => (
                        <div key={i} className="flex items-center justify-between px-3 py-1.5 text-xs">
                          <span className="text-slate-700 truncate">{s.name}</span>
                          <span className="text-slate-400 shrink-0 ml-2">{s.email}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent></Card>
            )}
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={runUnreachDryRun} className="text-xs" data-testid="unreach-rescan-btn">
                <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Re-scan
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="sm" disabled={unreachPurging || (unreach.unreachable_partners === 0 && unreach.incomplete_investors === 0)}
                    className="text-xs bg-rose-600 hover:bg-rose-700 text-white" data-testid="unreach-purge-btn">
                    {unreachPurging ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 mr-1.5" />}
                    Remove {unreach.unreachable_partners + unreach.incomplete_investors} unreachable records
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Remove {unreach.unreachable_partners} commercial partner(s) and {unreach.incomplete_investors} investor(s)?</AlertDialogTitle>
                    <AlertDialogDescription>
                      They have no email (or no name) so they cannot be reached in outreach.
                      Full records are archived to <code>unreachable_purge_archive</code> first.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="unreach-cancel-btn">Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={executeUnreachPurge} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="unreach-confirm-btn">
                      Remove permanently
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
            {unreachResult && !unreachResult.dry_run && (
              <Card className="border-emerald-200 bg-emerald-50/40 mt-4" data-testid="unreach-result-card">
                <CardContent className="p-4 text-sm text-slate-700">
                  <p className="font-semibold text-emerald-800 flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Purge complete</p>
                  <p>Commercial partners removed: <b>{unreachResult.deleted?.commercial_partners}</b> · Investors removed: <b>{unreachResult.deleted?.investors}</b></p>
                  <p>Investors: {unreachResult.before?.investors} → <b>{unreachResult.after?.investors}</b> · Partners: {unreachResult.before?.commercial_partners} → <b>{unreachResult.after?.commercial_partners}</b></p>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>

      {/* ===== Platform-only assets ===== */}
      <div className="pt-4 border-t border-slate-100" data-testid="platform-assets-section">
        <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <FlaskConical className="w-5 h-5 text-rose-500" /> Platform-Only Assets
        </h2>
        <p className="text-sm text-slate-500 mt-1 mb-3">
          ForesightRx tracks licensable therapeutic assets only. This removes standalone technology-platform
          records from the asset database — drug-discovery engines, delivery/formulation platforms,
          diagnostic/imaging/biomarker platforms and AI engines. Real drug assets that merely derive from a
          platform are kept. Archived to
          <code className="mx-1 text-xs bg-slate-100 px-1 rounded">platform_assets_archive</code> first.
        </p>
        {platLoading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : plat && (
          <>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <Card className="border-slate-100"><CardContent className="p-4">
                <p className="text-2xl font-bold text-rose-600" data-testid="plat-count">{plat.purgeable_assets}</p>
                <p className="text-xs text-slate-500">Platform assets to remove</p>
              </CardContent></Card>
              <Card className="border-slate-100"><CardContent className="p-4">
                <p className="text-2xl font-bold text-slate-900">{plat.before?.assets}</p>
                <p className="text-xs text-slate-500">Total assets (current)</p>
              </CardContent></Card>
              <Card className="border-slate-100"><CardContent className="p-4">
                <p className="text-2xl font-bold text-emerald-700">{plat.after?.assets}</p>
                <p className="text-xs text-slate-500">After purge</p>
              </CardContent></Card>
            </div>
            {(plat.sample || []).length > 0 ? (
              <Card className="border-slate-100 mb-4"><CardContent className="p-4">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Sample (first 80)</p>
                <div className="border border-slate-100 rounded-lg divide-y divide-slate-50 max-h-64 overflow-y-auto" data-testid="plat-sample">
                  {plat.sample.map((s, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-1.5 text-xs">
                      <span className="text-slate-700 truncate">{s.asset_name}</span>
                      <span className="text-slate-400 shrink-0 ml-2 truncate max-w-[45%]">{s.indication}</span>
                    </div>
                  ))}
                </div>
              </CardContent></Card>
            ) : (
              <p className="text-xs text-slate-400 flex items-center gap-1.5 mb-4" data-testid="plat-clean-msg">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> No platform-only asset records found.
              </p>
            )}
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={runPlatDryRun} className="text-xs" data-testid="plat-rescan-btn">
                <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Re-scan
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="sm" disabled={platPurging || plat.purgeable_assets === 0}
                    className="text-xs bg-rose-600 hover:bg-rose-700 text-white" data-testid="plat-purge-btn">
                    {platPurging ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 mr-1.5" />}
                    Remove {plat.purgeable_assets} platform assets
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Remove {plat.purgeable_assets} platform-only asset(s)?</AlertDialogTitle>
                    <AlertDialogDescription>
                      These technology-platform records are not licensable therapeutic assets and will be removed
                      from all asset counts, mappings and reports. Full records are archived to
                      <code> platform_assets_archive</code> first.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="plat-cancel-btn">Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={executePlatPurge} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="plat-confirm-btn">
                      Remove permanently
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
            {platResult && !platResult.dry_run && (
              <Card className="border-emerald-200 bg-emerald-50/40 mt-4" data-testid="plat-result-card">
                <CardContent className="p-4 text-sm text-slate-700">
                  <p className="font-semibold text-emerald-800 flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Purge complete</p>
                  <p>Platform assets removed: <b>{platResult.deleted?.assets}</b></p>
                  <p>Total assets: {platResult.before?.assets} → <b>{platResult.after?.assets}</b></p>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
}
